import { useContext } from 'react';
import { Alert, Box, Button, FormControl, FormLabel, Grid, IconButton, Typography } from '@mui/material';
import { ArrowBackIos, CalendarToday, Circle, CreditCard, Close, Refresh } from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import { TextField } from 'formik-mui';
import * as Yup from 'yup';
// Our Components
import { AuthContext } from 'context/auth-context';
import { CARD_FORM_STEP } from '../RequestCard';
import { CardContext } from 'context/cards/cardContext';
import CategoryInput from '../CategoryInput';
// Utils and Helpers
import { CARD_TYPE, REPLENISH_FRECUENCY } from 'helpers/constants';
import { hasCreditLine, hasCreditModel } from 'helpers/businesses';
import { numberFormat } from 'helpers/i18n';
import dayjs from 'helpers/dayjs';
// Assets
import physicalCardBackside from 'assets/cardsSection/cardBackgroud.svg';
import virtualCardBackside from 'assets/createCards/virtualCardBackside.png';

const validationSchema = Yup.object().shape({
    label: Yup.string().trim().max(100, 'Max 100 characters').required('Required'),
    memo: Yup.string().trim().max(255, 'Max 255 characters').optional()
});
const CardIdentity = ({ card, cardholder, onBackBtnClick, onOrderCardClick, onCardModalClose }) => {
    const { errorMessage } = useContext(CardContext);
    const { businessInfo } = useContext(AuthContext);

    const getReplenishFrequency = replenishFrequency => {
        return replenishFrequency === REPLENISH_FRECUENCY.ONE_TIME ? 'Manual' : 'Automatic';
    };

    const getBusinessAddress = () => {
        const { address } = businessInfo;
        const addressLine = address.line2 ? `${address.line1}, ${address.line2}` : `${address.line1}`;
        return (
            <Typography component="span" sx={{ fontWeight: '600', fontSize: '14px', color: '#182d55' }}>
                {addressLine} <br />
                {address.city}, {address.state}, {address.postalCode}
            </Typography>
        );
    };

    return (
        <Box className="request-card" sx={{ width: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                    <Box className="request-card__modal-title">Give your card an identity</Box>
                    <Box className="request-card__modal-paginator">
                        <span className="request-card__modal-page-indicator">3/3 Steps</span>
                        <Circle className="request-card__indicator-active" />
                        <Circle className="request-card__indicator-active" />
                        <Circle className="request-card__indicator-active" />
                    </Box>
                </Box>

                <Box alignSelf="start">
                    <IconButton onClick={onCardModalClose}>
                        <Close sx={{ fontSize: '20px', cursor: 'pointer' }} />
                    </IconButton>
                </Box>
            </Box>
            <Grid container columnSpacing={{ md: 3 }}>
                <Grid item md={6}>
                    <Box sx={{ marginBottom: '12px' }}>
                        <Formik
                            initialValues={card}
                            enableReinitialize={true}
                            validationSchema={validationSchema}
                            onSubmit={onOrderCardClick}>
                            {() => (
                                <Form id="card-identity-form">
                                    <FormControl fullWidth sx={{ marginBottom: '24px' }}>
                                        <FormLabel required className="request-card__card-input-label">
                                            Choose a category
                                        </FormLabel>
                                        <CategoryInput variant="outlined" name="label" size="small" />
                                    </FormControl>
                                    <FormControl fullWidth>
                                        <FormLabel className="request-card__card-input-label">Add a Memo (Optional)</FormLabel>
                                        <Field
                                            component={TextField}
                                            name="memo"
                                            multiline
                                            rows={4}
                                            size="small"
                                            placeholder="Add additional information about this card"
                                        />
                                    </FormControl>
                                </Form>
                            )}
                        </Formik>
                        <br></br>
                        <Typography component="span" className="request-card__card-banner-benefit">
                            {card.type === CARD_TYPE.VIRTUAL ? 'Billing Address' : 'Shipping Address'}
                        </Typography>
                        <br></br>
                        <Typography component="span" sx={{ fontWeight: '600', fontSize: '14px', color: '#182d55' }}>
                            {getBusinessAddress()}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item md={6}>
                    <Box className="request-card__card-banner-container">
                        <Box className="request-card__card-image-container" position="relative">
                            <img
                                className="request-card__card-image"
                                src={card.type === CARD_TYPE.VIRTUAL ? virtualCardBackside : physicalCardBackside}
                                alt="virtual card"
                            />
                            <Typography component="div" className="request-card__card-secret request-card__card-number">
                                **** **** **** ****
                            </Typography>
                            <Typography component="div" className="request-card__card-secret request-card__card-expiration">
                                ****
                            </Typography>
                            <Typography component="div" className="request-card__card-secret request-card__card-cvv">
                                ***
                            </Typography>

                            <Typography
                                component="div"
                                className="request-card__card-backside-cardholder-name embossing-ellipsis"
                                sx={{
                                    color: `${card.type === CARD_TYPE.VIRTUAL ? 'var(--white-color)' : 'var(--main-color)'}`
                                }}>
                                {`${cardholder?.first_name} ${cardholder?.last_name}`}
                            </Typography>
                            <Typography
                                component="div"
                                className="request-card__card-backside-cardholder-company embossing-ellipsis"
                                sx={{
                                    color: `${card.type === CARD_TYPE.VIRTUAL ? 'var(--white-color)' : 'var(--main-color)'}`
                                }}>
                                {businessInfo.legal_name}
                            </Typography>
                        </Box>
                        <Box className="request-card__card-banner-benefits">
                            <Box>
                                <Box display="flex" alignItems="center" marginBottom="22px">
                                    <CreditCard sx={{ marginRight: '14px' }} />
                                    <Typography component="span" className="request-card__card-banner-benefit">
                                        {hasCreditModel(businessInfo) && hasCreditLine(businessInfo)
                                            ? `Limit: ${numberFormat(card.limitAmount)}`
                                            : `Card Balance: ${numberFormat(card.limitAmount)}`}
                                    </Typography>
                                </Box>
                                {hasCreditModel(businessInfo) && hasCreditLine(businessInfo) && (
                                    <Box display="flex" alignItems="center" marginBottom="22px">
                                        <Refresh sx={{ marginRight: '14px' }} />
                                        <Typography component="span" className="request-card__card-banner-benefit">
                                            Replenishment: {getReplenishFrequency(card.has_manual_replenish)}
                                        </Typography>
                                    </Box>
                                )}
                                <Box display="flex" alignItems="center">
                                    <CalendarToday sx={{ marginRight: '14px' }} />
                                    <Typography component="span" className="request-card__card-banner-benefit">
                                        Lock Date: {card.autoLock ? dayjs(card.autoLock).format('DD/MM/YYYY') : 'N/A'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
            {errorMessage && (
                <Alert severity="error" sx={{ marginTop: '20px' }}>
                    {errorMessage}
                </Alert>
            )}
            <Box display="flex" justifyContent="space-between" sx={{ marginTop: '20px' }}>
                <Button variant="outlined" onClick={() => onBackBtnClick(CARD_FORM_STEP.CUSTOMIZE)}>
                    <ArrowBackIos fontSize="small" />
                </Button>
                <Button variant="contained" type="submit" form="card-identity-form">
                    Request Card
                </Button>
            </Box>
        </Box>
    );
};

export default CardIdentity;
